import React from 'react';
import { Heart, Users, Music, Leaf } from 'lucide-react';
import './About.css';

const About = () => {
  return (
    <div className="about-page">
      {/* Header */}
      <section className="about-header">
        <div className="container">
          <div className="about-header-content">
            <img src="/Logo.jpg" alt="Back to People" className="about-logo" />
            <h1>About Back to People</h1>
            <p className="about-tagline">
              A Reunion of Humans
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="about-mission section">
        <div className="container">
          <div className="about-mission-content">
            <h2>Our Mission</h2>
            <p className="about-lead">
              Back to People is a community-focused event series that brings people 
              together through meaningful celebrations, spiritual gatherings, and social 
              connection.
            </p>
            <p>
              We create spaces where people can come together authentically, celebrate life, 
              and build meaningful relationships. Through music, art, and conscious 
              celebration, we facilitate genuine human connection in a world that often 
              feels disconnected.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="about-philosophy section bg-black text-white">
        <div className="container">
          <h2>Our Philosophy</h2>
          <blockquote className="about-quote">
            "We don't organize these parties to clean up after other people. We do so to 
            create connection between people through mutual respect. If we can't respect 
            the earth, how can we respect each other?"
          </blockquote>
          <div className="about-values-grid">
            <div className="about-value">
              <Heart className="about-value-icon" />
              <h3>Authentic Connection</h3>
              <p>
                Be yourself, express your emotions, and connect genuinely with art, music, 
                and people. We believe in creating spaces where authenticity is celebrated.
              </p>
            </div>
            <div className="about-value">
              <Users className="about-value-icon" />
              <h3>Community Respect</h3>
              <p>
                Quality over quantity. We build relationships with respectful people who 
                understand that mutual respect creates lasting connection.
              </p>
            </div>
            <div className="about-value">
              <Music className="about-value-icon" />
              <h3>Art & Expression</h3>
              <p>
                Celebrating local artists giving back to the community through music and 
                art. Every event is a platform for creative expression and cultural celebration.
              </p>
            </div>
            <div className="about-value">
              <Leaf className="about-value-icon" />
              <h3>Environmental Responsibility</h3>
              <p>
                Leave No Trace principles guide everything we do. We're committed to 
                protecting the earth and the spaces where we gather.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-team section">
        <div className="container">
          <h2>The Team</h2>
          <div className="about-team-grid">
            <div className="about-team-member">
              <div className="about-team-photo">
                <div className="about-team-placeholder">
                  <Users size={64} />
                </div>
              </div>
              <h3>Rama</h3>
              <p className="about-team-role">Event Producer, Audio Rhythm Creator, Community Builder</p>
              <p className="about-team-bio">
                Co-hosting select events, Rama brings additional community reach and event 
                hosting expertise to create unforgettable celebrations.
              </p>
            </div>
            <div className="about-team-member">
              <div className="about-team-photo">
                <img src="/artists/zee.jpg" alt="Zee" />
              </div>
              <h3>Zee</h3>
              <p className="about-team-role">Event Producer, Visual Rhythm Creator, Community Builder</p>
              <p className="about-team-bio">
                AI content creator, event producer, and community builder bringing people 
                together through conscious celebrations. Zee creates immersive experiences 
                that blend technology, art, and human connection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record */}
      <section className="about-track-record section bg-gray">
        <div className="container">
          <h2>Proven Track Record</h2>
          <div className="about-achievements">
            <div className="about-achievement">
              <h3>October 18, 2025</h3>
              <h4>Back2People Coolum Beach</h4>
              <ul>
                <li>✅ Zero Waste - Complete "Leave No Trace" success</li>
                <li>✅ Professional Artists - Monzin, Facundo Prato, Felipe Supertino</li>
                <li>✅ Community Respect - All attendees followed guidelines</li>
                <li>✅ Venue Preservation - Location maintained in pristine condition</li>
              </ul>
            </div>
            <div className="about-achievement">
              <h3>November 1, 2025</h3>
              <h4>Halloween & Day of the Dead</h4>
              <ul>
                <li>Co-hosted with Rama at WetLands Free Space</li>
                <li>44-scene AI journey video production</li>
                <li>Expected attendance: 100-200 people</li>
                <li>Cultural respect and consciousness expansion focus</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Community Rules */}
      <section className="about-rules section">
        <div className="container">
          <h2>Community Rules & Standards</h2>
          <div className="about-rules-content">
            <div className="about-rule">
              <h3>1. Leave No Trace</h3>
              <p>Bring a rubbish bag, always bring your bag of love, take your rubbish with you</p>
            </div>
            <div className="about-rule">
              <h3>2. Respect the Earth</h3>
              <p>No littering, pack in pack out, leave it better than you found it</p>
            </div>
            <div className="about-rule">
              <h3>3. Respect Each Other</h3>
              <p>If we can't respect the earth, how can we respect each other?</p>
            </div>
            <div className="about-rule">
              <h3>4. Be Authentic</h3>
              <p>Don't try to be cool, just be cool and come party</p>
            </div>
            <div className="about-rule">
              <h3>5. Keep the Secret</h3>
              <p>The longer we keep places secret, the longer we can party every night</p>
            </div>
          </div>
          <p className="about-rules-footer">
            <em>"The longer we keep this sacred, the longer we can celebrate"</em>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta section bg-black text-white">
        <div className="container">
          <div className="about-cta-content">
            <h2>Join the Community</h2>
            <p>
              Ready to be part of something authentic? Subscribe to receive invitations 
              and stay connected with the B2P family.
            </p>
            <div className="about-cta-buttons">
              <a href="/subscribe" className="btn btn-white">
                Subscribe Now
              </a>
              <a href="/events" className="btn btn-outline btn-white">
                View Events
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

